<template>
  <div class="sync-health-overlay" @click.self="$emit('close')">
    <div class="sync-health-modal modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Sinkronizimi i sistemit</p>
        <button class="delete" aria-label="close" @click="$emit('close')"></button>
      </header>

      <section class="modal-card-body">
        <div v-if="loading" class="has-text-centered py-4">
          <i class="fa fa-spinner fa-spin"></i> Po analizohet katalogu...
        </div>
        <div v-else-if="error" class="notification is-danger is-light">{{ error }}</div>
        <div v-else-if="report">
          <p class="is-size-7 has-text-grey mb-3">
            Kontroll i plotë i katalogut — të gjitha produktet (aktiv, jo aktiv, në web, delistuar). Financa 5 kontrollohet vetëm për produktet aktive me kod.
          </p>

          <div class="tabs is-boxed is-small mb-3">
            <ul>
              <li :class="{ 'is-active': activeTab === 'catalog' }">
                <a @click.prevent="activeTab = 'catalog'">Katalogu</a>
              </li>
              <li :class="{ 'is-active': activeTab === 'financa5' }">
                <a @click.prevent="activeTab = 'financa5'">Financa 5</a>
              </li>
              <li :class="{ 'is-active': activeTab === 'quality' }">
                <a @click.prevent="activeTab = 'quality'">Cilësia e të dhënave</a>
              </li>
              <li :class="{ 'is-active': activeTab === 'inconsistencies' }">
                <a @click.prevent="activeTab = 'inconsistencies'">Mospërputhje</a>
              </li>
              <li :class="{ 'is-active': activeTab === 'products' }">
                <a @click.prevent="activeTab = 'products'">Produktet ({{ productsAudit.length }})</a>
              </li>
            </ul>
          </div>

          <!-- Catalog tab -->
          <div v-show="activeTab === 'catalog'">
            <div class="columns is-multiline mb-3">
              <div class="column is-4" v-for="card in catalogCards" :key="card.key">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">{{ card.label }}</p>
                  <p class="title is-5 mb-0" :class="card.class">{{ card.count }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Financa 5 tab -->
          <div v-show="activeTab === 'financa5'">
            <div class="columns is-multiline mb-3">
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Stoku Financa 5</p>
                  <p>
                    <span :class="f5.meta.stock_ok ? 'has-text-success' : 'has-text-danger'">{{ f5.meta.stock_ok ? "OK" : "Problem" }}</span>
                    — {{ f5.meta.stock_map_size }} kode
                  </p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Çmimet Financa 5</p>
                  <p>
                    <span :class="f5.meta.price_ok ? 'has-text-success' : 'has-text-danger'">{{ f5.meta.price_ok ? "OK" : "Problem" }}</span>
                    — {{ f5.meta.price_map_size }} kode
                  </p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Redis / ERP</p>
                  <p :class="f5.meta.redis_ok ? 'has-text-success' : 'has-text-danger'">{{ f5.meta.redis_ok ? "OK" : "Problem" }}</p>
                  <p class="is-size-7 has-text-grey" v-if="f5.meta.base_url">{{ f5.meta.base_url }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Kontrolluar (aktiv + kod)</p>
                  <p class="title is-5 mb-0">{{ f5.summary.total_checked_products || 0 }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Përputhje stoku</p>
                  <p class="title is-5 mb-0">{{ f5.summary.stock_matched_percent }}%</p>
                  <p class="is-size-7">{{ f5.summary.stock_matched_count }} / {{ f5.summary.total_checked_products }}</p>
                </div>
              </div>
              <div class="column is-4">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">Përputhje çmimi</p>
                  <p class="title is-5 mb-0">{{ f5.summary.price_matched_percent }}%</p>
                  <p class="is-size-7">{{ f5.summary.price_matched_count }} / {{ f5.summary.total_checked_products }}</p>
                </div>
              </div>
            </div>

            <sync-health-list-section
              title="Kode pa stok në Financa 5"
              :items="f5.missing_stock_codes"
              :total-count="f5MissingStockCount"
              :exporting="exportingSection === 'missing_stock_codes'"
              format="lines"
              @copy="copyLines(f5.missing_stock_codes)"
              @export="exportSection('missing_stock_codes', 'Pa stok Financa 5', 'lines')"
            />
            <sync-health-list-section
              v-if="f5MissingPriceCount > 0"
              title="Kode pa çmim në Financa 5"
              :items="f5.missing_price_codes"
              :total-count="f5MissingPriceCount"
              :exporting="exportingSection === 'missing_price_codes'"
              format="lines"
              class="mt-4"
              @copy="copyLines(f5.missing_price_codes)"
              @export="exportSection('missing_price_codes', 'Pa çmim Financa 5', 'lines')"
            />

            <div v-if="f5.composite_issues?.length" class="sync-section mt-4">
              <h4 class="title is-6">Paketa kompozite — problem stoku ({{ f5.composite_issues.length }})</h4>
              <div v-for="(issue, i) in f5.composite_issues" :key="i" class="box py-2 mb-2 is-size-7">
                <strong>{{ issue.code }}</strong> — {{ issue.name }}
                <ul class="mt-1 ml-3">
                  <li v-for="(c, j) in issue.components" :key="j">{{ c.code }} × {{ c.quantity }} — F5: {{ c.in_erp ? c.erp_stock : "mungon" }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Data quality tab -->
          <div v-show="activeTab === 'quality'">
            <div class="columns is-multiline mb-3">
              <div class="column is-4" v-for="card in qualityCards" :key="card.key">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">{{ card.label }}</p>
                  <p class="title is-5 mb-0" :class="card.count > 0 ? 'has-text-danger' : 'has-text-success'">{{ card.count }}</p>
                </div>
              </div>
            </div>

            <sync-health-list-section title="Pa kod" :items="quality.missing_code" :total-count="quality.summary.missing_code_count" :exporting="exportingSection === 'missing_code'" format="products" @copy="copyProducts(quality.missing_code)" @export="exportSection('missing_code', 'Pa kod')" />
            <sync-health-list-section title="Pa barkod" :items="quality.missing_barcode" :total-count="quality.summary.missing_barcode_count" :exporting="exportingSection === 'missing_barcode'" format="products" class="mt-4" @copy="copyProducts(quality.missing_barcode)" @export="exportSection('missing_barcode', 'Pa barkod')" />
            <sync-health-list-section title="Pa çmim në sistem" :items="quality.missing_price" :total-count="quality.summary.missing_price_count" :exporting="exportingSection === 'missing_price'" format="products" class="mt-4" @copy="copyProducts(quality.missing_price)" @export="exportSection('missing_price', 'Pa çmim në sistem')" />
            <sync-health-list-section title="Pa kategori produkti" :items="quality.missing_category" :total-count="quality.summary.missing_category_count" :exporting="exportingSection === 'missing_category'" format="products" class="mt-4" @copy="copyProducts(quality.missing_category)" @export="exportSection('missing_category', 'Pa kategori produkti')" />
            <sync-health-list-section title="Pa markë" :items="quality.missing_brand" :total-count="quality.summary.missing_brand_count" :exporting="exportingSection === 'missing_brand'" format="products" class="mt-4" @copy="copyProducts(quality.missing_brand)" @export="exportSection('missing_brand', 'Pa markë')" />
            <sync-health-list-section title="Pa njësi" :items="quality.missing_unit" :total-count="quality.summary.missing_unit_count" :exporting="exportingSection === 'missing_unit'" format="products" class="mt-4" @copy="copyProducts(quality.missing_unit)" @export="exportSection('missing_unit', 'Pa njësi')" />
            <sync-health-list-section title="Pa foto" :items="quality.missing_images" :total-count="quality.summary.missing_images_count" :exporting="exportingSection === 'missing_images'" format="products" class="mt-4" @copy="copyProducts(quality.missing_images)" @export="exportSection('missing_images', 'Pa foto')" />
            <sync-health-list-section title="Paketë kompozite pa komponentë" :items="quality.composite_no_components" :total-count="quality.summary.composite_no_components_count" :exporting="exportingSection === 'composite_no_components'" format="products" class="mt-4" @copy="copyProducts(quality.composite_no_components)" @export="exportSection('composite_no_components', 'Paketë bosh')" />
            <sync-health-list-section title="Pa atribute në kategori" :items="quality.no_category_attributes" :total-count="quality.summary.no_category_attributes_count" :exporting="exportingSection === 'no_category_attributes'" format="attribute_issues" class="mt-4" @copy="copyAttributeIssues(quality.no_category_attributes)" @export="exportSection('no_category_attributes', 'Pa atribute në kategori')" />
            <sync-health-list-section title="Atribute të paplotësuara" :items="quality.unfilled_attributes" :total-count="quality.summary.unfilled_attributes_count" :exporting="exportingSection === 'unfilled_attributes'" format="attribute_issues" class="mt-4" @copy="copyAttributeIssues(quality.unfilled_attributes)" @export="exportSection('unfilled_attributes', 'Atribute të paplotësuara')" />
            <sync-health-list-section title="Atribute të detyrueshme bosh" :items="quality.unfilled_required" :total-count="quality.summary.unfilled_required_count" :exporting="exportingSection === 'unfilled_required'" format="attribute_issues" class="mt-4" @copy="copyAttributeIssues(quality.unfilled_required)" @export="exportSection('unfilled_required', 'Atribute të detyrueshme bosh')" />
            <sync-health-list-section title="Pa kategori web (produkte në web)" :items="quality.missing_web_categories" :total-count="quality.summary.missing_web_categories_count" :exporting="exportingSection === 'missing_web_categories'" format="web_categories" class="mt-4" @copy="copyWebCategoryIssues(quality.missing_web_categories)" @export="exportSection('missing_web_categories', 'Pa kategori web')" />
          </div>

          <!-- Inconsistencies tab -->
          <div v-show="activeTab === 'inconsistencies'">
            <div class="columns is-multiline mb-3">
              <div class="column is-3" v-for="card in inconsistencyCards" :key="card.key">
                <div class="box py-2 mb-0">
                  <p class="heading mb-1">{{ card.label }}</p>
                  <p class="title is-5 mb-0" :class="card.count > 0 ? 'has-text-danger' : 'has-text-success'">{{ card.count }}</p>
                </div>
              </div>
            </div>

            <sync-health-list-section title="Jo sinkronizuar me ERP (fis_status = 0)" :items="inconsistencies.not_synced_erp" :total-count="inconsistencies.summary.not_synced_erp_count" :exporting="exportingSection === 'not_synced_erp'" format="products" @copy="copyProducts(inconsistencies.not_synced_erp)" @export="exportSection('not_synced_erp', 'Jo sinkronizuar ERP')" />
            <sync-health-list-section title="Delistuar por ende në web" :items="inconsistencies.delisted_in_web" :total-count="inconsistencies.summary.delisted_in_web_count" :exporting="exportingSection === 'delisted_in_web'" format="products" class="mt-4" @copy="copyProducts(inconsistencies.delisted_in_web)" @export="exportSection('delisted_in_web', 'Delistuar në web')" />
            <sync-health-list-section title="Jo aktiv por në web" :items="inconsistencies.inactive_in_web" :total-count="inconsistencies.summary.inactive_in_web_count" :exporting="exportingSection === 'inactive_in_web'" format="products" class="mt-4" @copy="copyProducts(inconsistencies.inactive_in_web)" @export="exportSection('inactive_in_web', 'Jo aktiv në web')" />
            <sync-health-list-section title="Kod produkti i përsëritur" :items="duplicateItems" :total-count="inconsistencies.summary.duplicate_code_products_count" :exporting="exportingSection === 'duplicate_code_products'" format="duplicate_codes" class="mt-4" @copy="copyDuplicateItems" @export="exportSection('duplicate_code_products', 'Kod i përsëritur')" />
          </div>

          <!-- Products audit tab -->
          <div v-show="activeTab === 'products'">
            <div class="field is-grouped mb-3">
              <div class="control is-expanded has-icons-left">
                <input v-model="productFilter" class="input is-small" type="text" placeholder="Filtro sipas kodit ose emrit..." />
                <span class="icon is-small is-left"><i class="fa fa-search"></i></span>
              </div>
              <div class="control">
                <div class="select is-small">
                  <select v-model="issueFilter">
                    <option value="">Të gjitha</option>
                    <option value="issues">Vetëm me probleme</option>
                    <option value="erp">Jo sinkronizuar ERP</option>
                    <option value="f5">Problem Financa 5</option>
                    <option value="web">Në web</option>
                  </select>
                </div>
              </div>
            </div>
            <div class="table-container sync-scroll-box sync-scroll-box-tall">
              <table class="table is-fullwidth is-striped is-narrow is-size-7 mb-0 sync-sticky-table">
                <thead>
                  <tr>
                    <th>Kodi</th>
                    <th>Emri</th>
                    <th>Aktiv</th>
                    <th>Web</th>
                    <th>ERP</th>
                    <th>Stok F5</th>
                    <th>Çmim F5</th>
                    <th>Foto</th>
                    <th>Probleme</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, i) in filteredProductsAudit" :key="row.guid || i">
                    <td>{{ row.code || "—" }}</td>
                    <td>{{ row.name }}</td>
                    <td :class="row.status ? 'has-text-success' : 'has-text-grey'">{{ row.status ? "Po" : "Jo" }}</td>
                    <td>{{ row.in_web ? "Po" : "Jo" }}</td>
                    <td :class="row.fis_status ? 'has-text-success' : 'has-text-danger'">{{ row.fis_status ? "Po" : "Jo" }}</td>
                    <td>{{ row.f5_reason === 'skipped' ? "—" : (row.f5_stock != null ? row.f5_stock : "—") }}</td>
                    <td>{{ row.f5_reason === 'skipped' ? "—" : (row.f5_price != null ? row.f5_price : "—") }}</td>
                    <td :class="row.has_images ? 'has-text-success' : 'has-text-danger'">{{ row.has_images ? "Po" : "Jo" }}</td>
                    <td>
                      <span v-if="!(row.system_issues || []).length" class="has-text-success">—</span>
                      <span v-else class="has-text-danger">{{ formatSystemIssues(row) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="is-size-7 has-text-grey mt-2">{{ filteredProductsAudit.length }} / {{ productsAudit.length }} produkte</p>
          </div>

          <p v-if="copyMessage" class="notification is-success is-light is-size-7 mt-3 py-2">{{ copyMessage }}</p>
          <p v-if="exportError" class="notification is-danger is-light is-size-7 mt-3 py-2">{{ exportError }}</p>
          <p class="is-size-7 has-text-grey mt-2">Gjeneruar: {{ report.generated_at }}</p>
        </div>
      </section>

      <footer class="modal-card-foot">
        <button type="button" class="button" @click="$emit('close')">Mbyll</button>
        <button type="button" class="button is-light" :disabled="loading || exportingExcel" @click="downloadExcel">
          <i v-if="exportingExcel" class="fa fa-spinner fa-spin mr-1"></i>
          <i v-else class="fa fa-file-excel-o mr-1"></i>
          Shkarko Excel
        </button>
        <button type="button" class="button is-link" :disabled="loading || exportingExcel" @click="load">Rifresko</button>
      </footer>
    </div>
  </div>
</template>

<script>
import ProductsServices from "@/services/Product";
import SyncHealthListSection from "./sync-health-list-section.vue";
import { exportSyncHealthSection } from "@/utils/syncHealthExport";

export default {
  components: { SyncHealthListSection },
  emits: ["close"],
  data() {
    return {
      loading: true,
      error: null,
      report: null,
      activeTab: "catalog",
      productFilter: "",
      issueFilter: "",
      copyMessage: "",
      copyMessageTimer: null,
      exportError: "",
      exportingExcel: false,
      exportingSection: "",
    };
  },
  computed: {
    catalog() {
      return this.report?.catalog?.summary || {};
    },
    f5() {
      return this.report?.financa5 || { meta: {}, summary: {}, missing_stock_codes: [], missing_price_codes: [], composite_issues: [] };
    },
    quality() {
      return this.report?.data_quality || { summary: {}, missing_code: [], missing_barcode: [] };
    },
    inconsistencies() {
      return this.report?.inconsistencies || { summary: {}, not_synced_erp: [], delisted_in_web: [], inactive_in_web: [], duplicate_code_products: [] };
    },
    productsAudit() {
      return this.report?.products_audit || [];
    },
    catalogCards() {
      const s = this.catalog;
      return [
        { key: "total", label: "Gjithsej", count: s.total_products || 0, class: "has-text-info" },
        { key: "active", label: "Aktiv", count: s.active_count || 0, class: "has-text-success" },
        { key: "inactive", label: "Jo aktiv", count: s.inactive_count || 0, class: "" },
        { key: "web", label: "Në web", count: s.in_web_count || 0, class: "" },
        { key: "delisted", label: "Delistuar", count: s.delisted_count || 0, class: s.delisted_count ? "has-text-danger" : "" },
        { key: "composite", label: "Kompozit", count: s.composite_count || 0, class: "" },
        { key: "dup", label: "Kode të përsëritura", count: s.duplicate_code_count || 0, class: s.duplicate_code_count ? "has-text-danger" : "has-text-success" },
      ];
    },
    qualityCards() {
      const s = this.quality.summary || {};
      return [
        { key: "code", label: "Pa kod", count: s.missing_code_count || 0 },
        { key: "barcode", label: "Pa barkod", count: s.missing_barcode_count || 0 },
        { key: "price", label: "Pa çmim", count: s.missing_price_count || 0 },
        { key: "cat", label: "Pa kategori", count: s.missing_category_count || 0 },
        { key: "brand", label: "Pa markë", count: s.missing_brand_count || 0 },
        { key: "unit", label: "Pa njësi", count: s.missing_unit_count || 0 },
        { key: "img", label: "Pa foto", count: s.missing_images_count || 0 },
        { key: "req", label: "Atribute të detyrueshme bosh", count: s.unfilled_required_count || 0 },
      ];
    },
    inconsistencyCards() {
      const s = this.inconsistencies.summary || {};
      return [
        { key: "erp", label: "Jo sinkronizuar ERP", count: s.not_synced_erp_count || 0 },
        { key: "del", label: "Delistuar në web", count: s.delisted_in_web_count || 0 },
        { key: "inact", label: "Jo aktiv në web", count: s.inactive_in_web_count || 0 },
        { key: "dup", label: "Kod i përsëritur", count: s.duplicate_code_products_count || 0 },
      ];
    },
    duplicateItems() {
      return this.inconsistencies.duplicate_code_products || [];
    },
    f5MissingStockCount() {
      return this.productsAudit.filter((p) => p.f5_reason === "code_not_in_financa5").length;
    },
    f5MissingPriceCount() {
      return this.productsAudit.filter(
        (p) => p.f5_reason !== "skipped" && p.f5_reason !== "empty_product_code" && p.code && !p.f5_price_ok
      ).length;
    },
    filteredProductsAudit() {
      const q = (this.productFilter || "").trim().toLowerCase();
      return this.productsAudit.filter((row) => {
        if (q) {
          const code = String(row.code || "").toLowerCase();
          const name = String(row.name || "").toLowerCase();
          if (!code.includes(q) && !name.includes(q)) return false;
        }
        if (this.issueFilter === "issues") return (row.system_issues || []).length > 0;
        if (this.issueFilter === "erp") return !row.fis_status && row.status;
        if (this.issueFilter === "f5") return row.f5_reason && row.f5_reason !== "ok" && row.f5_reason !== "skipped";
        if (this.issueFilter === "web") return row.in_web;
        return true;
      });
    },
  },
  async mounted() {
    await this.load();
  },
  beforeUnmount() {
    if (this.copyMessageTimer) clearTimeout(this.copyMessageTimer);
  },
  methods: {
    formatSystemIssues(row) {
      const issues = [...(row.system_issues || [])];
      if ((row.missing_required_attributes || []).length) {
        issues.push(`Detyrueshme: ${row.missing_required_attributes.join(", ")}`);
      }
      return issues.join("; ") || "—";
    },
    showCopyFeedback(message) {
      this.copyMessage = message;
      if (this.copyMessageTimer) clearTimeout(this.copyMessageTimer);
      this.copyMessageTimer = setTimeout(() => { this.copyMessage = ""; }, 2500);
    },
    async copyToClipboard(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.showCopyFeedback("Lista u kopjua.");
      } catch (e) {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        this.showCopyFeedback("Lista u kopjua.");
      }
    },
    copyLines(lines) {
      this.copyToClipboard((lines || []).join("\n"));
    },
    copyProducts(products) {
      this.copyToClipboard((products || []).map((p) => `${p.code || "—"}\t${p.name || ""}`).join("\n"));
    },
    copyAttributeIssues(items) {
      const text = (items || []).map((p) => {
        const missing = (p.missing || []).join(", ");
        const reason = p.reason ? ` (${p.reason})` : "";
        return `${p.code || "—"}\t${p.name || ""}${missing ? `\t→ ${missing}` : reason}`;
      }).join("\n");
      this.copyToClipboard(text);
    },
    copyWebCategoryIssues(items) {
      const text = (items || []).map((p) => {
        const eff = [p.effective_web_cat_1, p.effective_web_cat_2, p.effective_web_cat_3].filter((v) => v != null && String(v).trim() !== "").join(" > ");
        return `${p.code || "—"}\t${p.name || ""}\t${eff || "—"}`;
      }).join("\n");
      this.copyToClipboard(text);
    },
    copyDuplicateItems() {
      const text = this.duplicateItems.map((p) => `${p.code || "—"}\t${p.name || ""}\t×${p.duplicate_count || 2}`).join("\n");
      this.copyToClipboard(text);
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        this.report = await ProductsServices.getSystemSyncHealth();
      } catch (e) {
        this.error = e?.response?.data?.message || "Gabim gjatë leximit të raportit.";
      } finally {
        this.loading = false;
      }
    },
    async downloadExcel() {
      this.exportError = "";
      this.exportingExcel = true;
      try {
        await ProductsServices.exportSystemSyncHealth();
        this.showCopyFeedback("Excel u shkarkua.");
      } catch (e) {
        this.exportError = e?.message || "Gabim gjatë shkarkimit të Excel.";
      } finally {
        this.exportingExcel = false;
      }
    },
    exportSection(sectionKey, title, format) {
      this.exportError = "";
      this.exportingSection = sectionKey;
      try {
        exportSyncHealthSection(this.productsAudit, sectionKey, title, format);
        this.showCopyFeedback("Excel u shkarkua.");
      } catch (e) {
        this.exportError = e?.message || "Gabim gjatë shkarkimit të Excel.";
      } finally {
        this.exportingSection = "";
      }
    },
  },
};
</script>

<style scoped>
.sync-health-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(10, 10, 10, 0.52);
  overflow-y: auto;
}
.sync-health-modal {
  width: 100%;
  max-width: 1100px;
  max-height: calc(100vh - 3rem);
  display: flex;
  flex-direction: column;
  margin: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}
.sync-health-modal .modal-card-body {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.sync-scroll-box {
  max-height: 240px;
  overflow: auto;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}
.sync-scroll-box-tall {
  max-height: 400px;
}
.sync-sticky-table thead th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
  box-shadow: inset 0 -1px 0 #dbdbdb;
}
</style>
